export type ServiceAvailability =
  | { state: "available" }
  | { state: "retrying" | "unavailable"; service?: string };

type ServiceAvailabilityRequest = { key: string; generation: number };

type UnavailableEntry = Exclude<ServiceAvailability, { state: "available" }>;

const AVAILABLE: ServiceAvailability = { state: "available" };
const entries = new Map<string, UnavailableEntry>();
const listeners = new Set<() => void>();
let snapshot: ServiceAvailability = AVAILABLE;
let generation = 0;

function publish(): void {
  const all = [...entries.values()];
  snapshot = all.find(entry => entry.state === "retrying") ?? all[0] ?? AVAILABLE;
  for (const listener of listeners) listener();
}

function entry(state: UnavailableEntry["state"], service?: string): UnavailableEntry {
  return service === undefined ? { state } : { state, service };
}

function isStale(request: ServiceAvailabilityRequest): boolean {
  return request.generation !== generation;
}

export function currentServiceAvailabilityGeneration(): number {
  return generation;
}

export function markServiceRetrying(request: ServiceAvailabilityRequest, service?: string): void {
  if (isStale(request)) return;
  entries.set(request.key, entry("retrying", service));
  publish();
}

export function markServiceUnavailable(
  request: ServiceAvailabilityRequest,
  service?: string,
): void {
  if (isStale(request)) return;
  entries.set(request.key, entry("unavailable", service));
  publish();
}

export function markServiceAvailable(request: ServiceAvailabilityRequest): void {
  if (isStale(request)) return;
  if (entries.delete(request.key)) publish();
}

export function resetServiceAvailability(): void {
  generation++;
  entries.clear();
  publish();
}

export function subscribeServiceAvailability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getServiceAvailability(): ServiceAvailability {
  return snapshot;
}
